import { useEffect, useRef, type RefObject } from 'react';
import { shouldExpandNavSidebar } from './panelLayoutEngine';
import { useCanvasViewportStore } from '../stores/canvasViewportStore';
import { useLayoutStore } from '../stores/layoutStore';

const RECLAMP_DEBOUNCE_MS = 150;

/**
 * Observes `.agentable-canvas-root` size and debounces re-clamp of visible panels
 * when the embed container resizes (not just the browser window).
 */
export function useCanvasViewport(rootRef: RefObject<HTMLElement | null>): void {
  const setDimensions = useCanvasViewportStore((s) => s.setDimensions);
  const reclampVisiblePanels = useLayoutStore((s) => s.reclampVisiblePanels);
  const updateVoiceDock = useLayoutStore((s) => s.updateVoiceDock);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const applySize = (width: number, height: number): void => {
      setDimensions(width, height);

      const expanded = shouldExpandNavSidebar(width);
      const { navSidebarExpanded, setNavSidebarExpanded } =
        useLayoutStore.getState();
      if (navSidebarExpanded !== expanded) {
        setNavSidebarExpanded(expanded);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        reclampVisiblePanels();
        updateVoiceDock();
      }, RECLAMP_DEBOUNCE_MS);
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      applySize(width, height);
    });

    ro.observe(el);
    applySize(el.clientWidth, el.clientHeight);

    return () => {
      ro.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rootRef, setDimensions, reclampVisiblePanels, updateVoiceDock]);
}
